import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Mic2, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Login = () => {
  const navigate = useNavigate();
  const [emailPasswordData, setEmailPasswordData] = useState({ email: '', password: '' });
  const [otpEmail, setOtpEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    
    try {
      // Use relative URL for API calls (works with proxy in dev and same-origin in production)
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailPasswordData.email,
          password: emailPasswordData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Store token
        localStorage.setItem('access_token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        toast({ 
          title: "Login successful!", 
          description: "Welcome back to Mic Check Millionaire." 
        });
        
        // Redirect based on user type
        setTimeout(() => {
          if (data.user.user_type === 'reviewer') {
            navigate('/reviewer-dashboard');
          } else if (data.user.email === 'admin@miccheckmillionaire.com' || data.user.email === 'jxhn@miccheckm.com') {
            navigate('/admin');
          } else {
            navigate('/reviewers');
          }
        }, 1000);
      } else {
        toast({
          title: "Login failed",
          description: data.detail || "Invalid credentials.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Error",
        description: "Failed to login. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    try {
      // Use relative URL for API calls
      const response = await fetch('/api/auth/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpEmail })
      });

      // Safely parse response - handle non-JSON responses
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Non-JSON response (e.g., plain text error)
        const text = await response.text();
        console.error('OTP send error: Non-JSON response:', text);
        data = { 
          error: 'SERVER_ERROR', 
          detail: 'Server error. Please try again.' 
        };
      }

      // Check for success (either HTTP 200 with message, or check for error field)
      if (response.ok && data.message && !data.error) {
        setOtpSent(true);
        toast({ 
          title: "Success", 
          description: "We sent a login code to your email." 
        });
      } else if (response.status === 429 || data.error === 'RATE_LIMITED') {
        // Rate limit hit
        toast({
          title: "Too Many Attempts",
          description: data.detail || "Please wait and try again.",
          variant: "destructive"
        });
      } else {
        // Any other error
        console.error('OTP send failed:', data);
        toast({
          title: "Failed to Send Code",
          description: data.detail || "We couldn&apos;t send a code right now. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('OTP send error:', error);
      toast({
        title: "Error",
        description: "We couldn&apos;t send a code right now. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    try {
      // Use relative URL for API calls
      const response = await fetch('/api/auth/otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: otpEmail,
          otp: otp
        })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('access_token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        toast({ 
          title: "Login successful!", 
          description: "Welcome back to Mic Check Millionaire." 
        });
        
        setTimeout(() => {
          if (data.user.user_type === 'reviewer') {
            navigate('/reviewer-dashboard');
          } else {
            navigate('/reviewers');
          }
        }, 1000);
      } else {
        toast({
          title: "Verification failed",
          description: data.detail || "Invalid OTP.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('OTP verify error:', error);
      toast({
        title: "Error",
        description: "Failed to verify OTP. Please try again.",
        variant: "destructive"
      });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleGoogleLogin = () => {
    toast({ title: "Google login", description: "Redirecting to Google..." });
    // Mock Google OAuth
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 group">
            <div className="bg-black p-3 rounded-lg group-hover:bg-[#F2D857] transition-colors">
              <Mic2 className="h-8 w-8 text-[#F2D857] group-hover:text-black" />
            </div>
          </Link>
          <h2 className="mt-6 text-3xl font-bold font-montserrat">
            Welcome Back
          </h2>
          <p className="mt-2 text-gray-600">
            Sign in to continue to Mic Check Millionaire
          </p>
        </div>

        <Card className="p-8">
          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="email">Email / Password</TabsTrigger>
              <TabsTrigger value="otp">Email OTP</TabsTrigger>
            </TabsList>

            {/* Email/Password Tab */}
            <TabsContent value="email">
              <form onSubmit={handleEmailPasswordLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={emailPasswordData.email}
                      onChange={(e) => setEmailPasswordData({ ...emailPasswordData, email: e.target.value })}
                      className="pl-10 h-11"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={emailPasswordData.password}
                      onChange={(e) => setEmailPasswordData({ ...emailPasswordData, password: e.target.value })}
                      className="pl-10 pr-12 h-11"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-0 top-0 h-full w-12 flex items-center justify-center text-gray-400 hover:text-gray-600 active:text-gray-800 transition touch-manipulation"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <Link to="/forgot-password" className="text-sm text-[#F2D857] hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <Button type="submit" className="w-full bg-black hover:bg-[#F2D857] hover:text-black text-white h-11">
                  Sign In
                </Button>
              </form>
            </TabsContent>

            {/* OTP Tab */}
            <TabsContent value="otp">
              {!otpSent ? (
                <form onSubmit={handleSendOTP} className="space-y-4">
                  <div>
                    <Label htmlFor="otp-email">Email Address</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="otp-email"
                        type="email"
                        placeholder="you@example.com"
                        value={otpEmail}
                        onChange={(e) => setOtpEmail(e.target.value)}
                        className="pl-10 h-11"
                        required
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      We&apos;ll send you a one-time password to sign in.
                    </p>
                  </div>
                  <Button type="submit" className="w-full bg-black hover:bg-[#F2D857] hover:text-black text-white h-11">
                    Send OTP
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOTP} className="space-y-4">
                  <div>
                    <Label htmlFor="otp-code">Enter OTP</Label>
                    <Input
                      id="otp-code"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="h-11 text-center text-2xl tracking-widest"
                      maxLength={6}
                      required
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      Sent to {otpEmail}
                    </p>
                  </div>
                  <Button type="submit" className="w-full bg-black hover:bg-[#F2D857] hover:text-black text-white h-11">
                    Verify & Sign In
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setOtpSent(false)}
                    className="w-full"
                  >
                    Use different email
                  </Button>
                </form>
              )}
            </TabsContent>
          </Tabs>

          {/* Social Login - Hidden until fully implemented 
          <div className="mt-6">
            <Separator className="my-6" />
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full h-11 border-2"
            >
              <Chrome className="h-5 w-5 mr-2" />
              Continue with Google
            </Button>
          </div>
          */}
        </Card>

        {/* Sign Up Link */}
        <p className="mt-6 text-center text-gray-600">
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="text-[#F2D857] font-semibold hover:underline">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;